import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { InputNumberModule } from 'primeng/inputnumber';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { AppButtonComponent } from '@app/shared/components/app-button/app-button.component';
import { PermissionService } from '@app/core/services/permission.service';
import {
  ModApprovalThresholdConfig,
  OrderModificationApprovalService,
} from '@app/feature/center-business/order/services/order-modification-approval.service';

@Component({
  selector: 'app-modification-approval-threshold',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    ToastModule,
    InputNumberModule,
    ToggleSwitchModule,
    AppButtonComponent,
  ],
  providers: [MessageService],
  templateUrl: './modification-approval-threshold.component.html',
  styleUrl: './modification-approval-threshold.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ModificationApprovalThresholdComponent implements OnInit {
  private readonly api = inject(OrderModificationApprovalService);
  private readonly permissionService = inject(PermissionService);
  private readonly messageService = inject(MessageService);
  private readonly translate = inject(TranslateService);

  private readonly permissionCodes = toSignal(
    this.permissionService.userPermissions$,
    { initialValue: this.permissionService.getCurrentPermissions() },
  );

  canView = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction(
      'settings.order.modification_approval_threshold.view',
    );
  });

  canEdit = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction(
      'settings.order.modification_approval_threshold.edit',
    );
  });

  loading = signal(false);
  saving = signal(false);
  increaseL3Percent = signal(20);
  decreaseL3Percent = signal(30);
  highValueItemAmount = signal<number | null>(5_000_000);
  enabled = signal(true);
  configId = signal<number | undefined>(undefined);

  ngOnInit(): void {
    if (!this.canView()) {
      return;
    }
    this.load();
  }

  load(): void {
    this.loading.set(true);
    this.api.getThresholdConfig().subscribe({
      next: (cfg: ModApprovalThresholdConfig) => {
        this.configId.set(cfg.id);
        this.increaseL3Percent.set(
          Math.round(Number(cfg.increaseL3Ratio ?? 0.2) * 100),
        );
        this.decreaseL3Percent.set(
          Math.round(Number(cfg.decreaseL3Ratio ?? 0.3) * 100),
        );
        this.highValueItemAmount.set(
          cfg.highValueItemAmount == null
            ? null
            : Number(cfg.highValueItemAmount),
        );
        this.enabled.set(cfg.enabled !== false);
        this.loading.set(false);
      },
      error: () => {
        this.loading.set(false);
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('common.messages.error'),
          detail: this.translate.instant(
            'modification_approval_threshold.messages.load_failed',
          ),
        });
      },
    });
  }

  save(): void {
    if (!this.canEdit()) {
      return;
    }
    const increase = this.increaseL3Percent();
    const decrease = this.decreaseL3Percent();
    if (!increase || increase < 1 || increase > 100 || !decrease || decrease < 1 || decrease > 100) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translate.instant('common.messages.warning'),
        detail: this.translate.instant(
          'modification_approval_threshold.messages.invalid_ratio',
        ),
      });
      return;
    }
    this.saving.set(true);
    this.api
      .upsertThresholdConfig({
        id: this.configId(),
        increaseL3Ratio: increase / 100,
        decreaseL3Ratio: decrease / 100,
        highValueItemAmount: this.highValueItemAmount() ?? undefined,
        enabled: this.enabled(),
      })
      .subscribe({
        next: (cfg) => {
          this.configId.set(cfg.id);
          this.saving.set(false);
          this.messageService.add({
            severity: 'success',
            summary: this.translate.instant('common.messages.success'),
            detail: this.translate.instant(
              'modification_approval_threshold.messages.save_success',
            ),
          });
        },
        error: () => {
          this.saving.set(false);
          this.messageService.add({
            severity: 'error',
            summary: this.translate.instant('common.messages.error'),
            detail: this.translate.instant(
              'modification_approval_threshold.messages.save_failed',
            ),
          });
        },
      });
  }
}
